import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, LogIn, Users, ChevronRight, Trophy } from 'lucide-react';
import { getGroups, createGroup, joinGroup, getSystems } from '../api';
import { useToast } from '../components/Toast';

function CreateGroupModal({ onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [systemId, setSystemId] = useState('');
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSystems()
      .then(res => setSystems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !systemId) return;
    setSaving(true);
    try {
      const res = await createGroup({ name: name.trim(), systemId });
      toast.success(`Group created! Invite code: ${res.data.inviteCode}`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create group');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal__overlay" onClick={onClose}>
      <div className="modal__content" onClick={e => e.stopPropagation()}>
        <h2>CREATE GROUP</h2>
        <form onSubmit={handleSubmit}>
          <div className="form__group">
            <label>Group Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Weekend Warriors" required maxLength={50} autoFocus />
          </div>
          <div className="form__group">
            <label>Trail System *</label>
            {loading ? (
              <div className="loading loading--sm"><span className="spinner" /></div>
            ) : (
              <select value={systemId} onChange={e => setSystemId(e.target.value)} required className="filter-bar__select" style={{ width: '100%' }}>
                <option value="">Select a system...</option>
                {systems.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="form__actions">
            <button type="button" className="btn btn--secondary flex-1" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary flex-1" disabled={saving || !name.trim() || !systemId}>
              {saving ? <span className="spinner" /> : 'CREATE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JoinGroupModal({ onClose, onJoined }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    try {
      await joinGroup(code.trim());
      toast.success('Joined group!');
      onJoined();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to join group');
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="modal__overlay" onClick={onClose}>
      <div className="modal__content" onClick={e => e.stopPropagation()}>
        <h2>JOIN GROUP</h2>
        <form onSubmit={handleSubmit}>
          <div className="form__group">
            <label>Invite Code</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. A1B2C3"
              maxLength={6}
              autoFocus
              style={{ letterSpacing: '0.15em', fontWeight: 600, textAlign: 'center', fontSize: '18px' }}
            />
          </div>
          <div className="form__actions">
            <button type="button" className="btn btn--secondary flex-1" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary flex-1" disabled={joining || code.trim().length < 6}>
              {joining ? <span className="spinner" /> : 'JOIN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);

  const load = useCallback(() => {
    getGroups()
      .then(res => setGroups(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <div className="fade-in">
      <div className="page-header__row" style={{ marginBottom: '28px' }}>
        <div>
          <h1 className="page-header__title">GROUPS</h1>
          <p className="page-header__subtitle">
            {groups.length} group{groups.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="groups-header__actions">
          <button className="btn btn--secondary" onClick={() => setShowJoin(true)}>
            <LogIn size={14} /> JOIN
          </button>
          <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> CREATE
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner spinner--lg" /></div>
      ) : groups.length === 0 ? (
        <div className="empty-state">
          <p>No groups yet. Create one and invite friends to compete on a trail system!</p>
          <div className="empty-state__actions">
            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Create Group
            </button>
            <button className="btn btn--secondary" onClick={() => setShowJoin(true)}>
              <LogIn size={14} /> Join with Code
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid--gap-lg">
          {groups.map(group => (
            <Link to={`/groups/${group._id}`} key={group._id} className="card group-card">
              <div className="group-card__icon">
                <Trophy size={20} />
              </div>
              <div className="flex-1">
                <div className="group-card__name">{group.name}</div>
                <div className="group-card__meta">
                  {group.system?.name || 'Unknown system'}
                  <span className="group-card__members">
                    <Users size={11} /> {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <ChevronRight size={16} className="text-muted" />
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {showJoin && <JoinGroupModal onClose={() => setShowJoin(false)} onJoined={load} />}
    </div>
  );
}
